import { useEffect, useRef } from "react";

/**
 * Linear-style global shortcuts per PRD §"Interaction principles":
 *   `c` create, `j`/`k` navigate, `e` edit, `?` help, plus two-key `g X` sequences
 *   for "go to" navigation (`g c` calendar, `g d` dispatch, `g l` day list,
 *   `g u` customers).
 *
 * The hook accepts a record of {key: handler}. Single-letter keys fire
 * immediately. Sequences are written as `"g c"` (space-separated) and fire when
 * the second key is pressed within 1.2 seconds of the first; pressing any other
 * key cancels the sequence.
 *
 * Shortcut keys are ignored while focus is in an input, textarea, select, or
 * contenteditable element.
 */

export type ShortcutMap = Record<string, (event: KeyboardEvent) => void>;

const SEQUENCE_TIMEOUT_MS = 1200;

function isEditing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function useKeyboardShortcuts(shortcuts: ShortcutMap, enabled = true): void {
  // useRef so the listener doesn't re-bind on every render (which would lose
  // the in-flight sequence prefix).
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;

    let prefix: string | null = null;
    let prefixTimer: ReturnType<typeof setTimeout> | null = null;

    function clearPrefix(): void {
      prefix = null;
      if (prefixTimer) {
        clearTimeout(prefixTimer);
        prefixTimer = null;
      }
    }

    const handler = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditing(event.target)) return;
      const key = event.key;

      // Continuing a sequence?
      if (prefix) {
        const composed = `${prefix} ${key}`;
        const fn = shortcutsRef.current[composed];
        clearPrefix();
        if (fn) {
          event.preventDefault();
          fn(event);
        }
        return;
      }

      // Start a sequence if any registered shortcut begins with `${key} `.
      const isPrefixForSomeShortcut = Object.keys(shortcutsRef.current).some(
        (s) => s.startsWith(`${key} `)
      );
      if (isPrefixForSomeShortcut) {
        prefix = key;
        prefixTimer = setTimeout(clearPrefix, SEQUENCE_TIMEOUT_MS);
        event.preventDefault();
        return;
      }

      // Single-key shortcut.
      const fn = shortcutsRef.current[key];
      if (!fn) return;
      event.preventDefault();
      fn(event);
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      clearPrefix();
    };
  }, [enabled]);
}
